import { Design, DesignChangeMap } from '@app/types';
import ChangesContainer from './ChangesContainer';
import Utility from '@app/utilities/Utility';
import _ from 'lodash';

type DesignChangeMapKey = keyof DesignChangeMap;

type DesignChangeMapElement = DesignChangeMap[DesignChangeMapKey];

export type ChangeSetWrapperProps = {
    clKey: keyof DesignChangeMap;
    filteredMap: DesignChangeMapElement | Record<string, any>;
    version: string;
    serialized?: Design;
};

export function ChangeSetWrapper({
  clKey,
  filteredMap,
  serialized,
  version
}: ChangeSetWrapperProps) {
  const label = Utility.getChangeLogLabel(clKey);
  const notMapped = ['globalErrorHandling', 'globalExceptionHandling', 'globalLoopHandling', 'intentHandling'];
  const simpleChanges = ['languages', 'applicationVersion'];
  const sortedChanges = returnSortedChanges(filteredMap, clKey) || Object.entries(filteredMap ?? {});

  /**
   * Settings specific rendering
   * @param {*} changeMap
   * @returns jsx
   */
  const getSettingsChangesContainer = (changeMap: any) => {
    const clonedSettings = _.cloneDeep(changeMap['settings']);
    const settingsPanels: any[] = [];

    if (clonedSettings['intentHandling']) {
      delete clonedSettings['intentHandling']['inARowWatched'];
      delete clonedSettings['intentHandling']['xAndOutWatched'];

      Object.keys(clonedSettings['intentHandling']).forEach((iKey) => {
        clonedSettings[iKey] = clonedSettings['intentHandling'][iKey];
      });
    }

    if (clonedSettings['globalLoopHandling']) {
      Object.keys(clonedSettings['globalLoopHandling']).forEach((iKey) => {
        clonedSettings[iKey] = clonedSettings['globalLoopHandling'][iKey];
      });

      delete clonedSettings['globalLoopHandling'];
    }

    Object.keys(clonedSettings)
      .sort((a, b) => (a > b ? 1 : -1))
      .forEach((key) => {
        const cl = changeMap['settings'][key];
        settingsPanels.push(
          <ChangesContainer
            changeMap={cl}
            key={key}
            label={Utility.getChangeLogLabel(key)}
            listType={key}
            serialized={serialized}
          />
        );
      });

    return <div>{settingsPanels}</div>;
  };

  if (clKey === 'settings') {
    return getSettingsChangesContainer(filteredMap);
  }

  if (simpleChanges.includes(clKey)) {
    return (
      <ChangesContainer
        changeMap={[clKey, sortedChanges]}
        key={clKey}
        label={label}
        listType={clKey}
        serialized={serialized}
        version={version}
      />
    );
  }

  if (notMapped.includes(clKey)) {
    return (
      <ChangesContainer
        changeMap={sortedChanges}
        key={clKey}
        label={label}
        listType={clKey}
        serialized={serialized}
        version={version}
      />
    );
  }

  if (!Array.isArray(sortedChanges)) {
    return null;
  }

  return (
    <>
      {sortedChanges.map((changeMap, i) => {
        return (
          <ChangesContainer
            changeMap={changeMap}
            key={i}
            label={label}
            listType={clKey}
            serialized={serialized}
            version={version}
          />
        );
      })}
    </>
  );
};

const returnSortedChanges = (changeMap: DesignChangeMapElement = {}, key: keyof DesignChangeMap) => {
  switch (key) {
    case 'ccxml':
    case 'modules':
      return Object.entries(changeMap);
    case 'application-modules':
    case 'application-objects':
      return Object.entries(filterExcluded(changeMap) ?? {})
        .sort((a, b) => ((a[1] as any).node.name < (b[1] as any).node.name ? 1 : -1))
        .sort((a, b) => ((a[1] as any).node._class > (b[1] as any).node._class ? 1 : -1));
    case 'application-variables':
      return Object.entries(changeMap).sort((a, b) => ((a[1] as any).node.name > (b[1] as any).node.name ? 1 : -1));
    case 'compoundIntents':
      return Object.entries(changeMap).sort((a, b) =>
        (a[1] as any).object.name < (b[1] as any).object.name ? 1 : -1
      );
    case 'csvData':
    case 'dialogs':
    case 'functions':
    case 'graphQLSchemas':
    case 'unitTests':
      return Object.entries(changeMap).sort((a, b) =>
        (a[1] as any).object.name > (b[1] as any).object.name ? 1 : -1
      );
    case 'application-intents':
      return Object.entries(changeMap).sort((a, b) => ((a[1] as any).node.name > (b[1] as any).node.name ? 1 : -1));
    case 'fillerPrompts':
      return Object.entries(changeMap).sort((a, b) => (a[0] > b[0] ? 1 : -1));
    case 'globalIntents':
      return Object.entries(changeMap).sort((a, b) =>
        (a[1] as any).object.moduleProperties.name > (b[1] as any).object.moduleProperties.name ? 1 : -1
      );
    case 'applicationVersion':
    case 'intentHandling':
    case 'languages':
    case 'settings':
      return changeMap;
  }
};

/**
 * 'excludeFromDisplay' seems to be deprecated
 * but keeping the filter in case an old design has it
 */
export function filterExcluded(
  changeMapElement: DesignChangeMapElement = {}
): DesignChangeMapElement {
  const changeMapElementArray = Object.entries(changeMapElement);
  return Object.fromEntries(
    changeMapElementArray.filter(([_, { excludeFromDisplay }]) => {
      return !excludeFromDisplay;
    })
  ) as DesignChangeMapElement;
};
